import { Vector2 } from 'three';
import { TextAreaObjectConfig } from '../interfaces/object_config';
import PlanogramBox from './PlanogramBox';
import { PLANOGRAM_CONFIG } from '../config/PlanogramConfig';
import { TextVerticalAlignment } from '../interfaces/planogram';
import type { TextRenderInfo, Text, TypesetParams } from 'troika-three-text';
import { getTextRenderInfo } from 'troika-three-text';

export enum TextAreaContentKeyActionType {
  PASTE = 'PASTE',
  CUT = 'CUT',
  RESET = 'RESET',
}

export type CaretQuad = [startX: number, endX: number, bottomY: number, topY: number];
export type CaretQuadArray = CaretQuad[];
export type CaretPositionalData = {
  position: Vector2;
  index: number;
};

export type CaretInputData = {
  caretPositions: CaretQuadArray;
  caretHeight: number;
};

// TODO: can eliminate this function and operate on the floats directly?
export const toCaretQuads = (floats: Float32Array): CaretQuadArray => {
  const quads: CaretQuadArray = [];
  for (let i = 0; i < floats.length; i += 4) {
    quads.push([floats[i], floats[i + 1], floats[i + 2], floats[i + 3]]);
  }
  // add phantom caret quad at the end to allow caret to be placed at the end of the text
  if (quads.length > 0) {
    const endX = quads[quads.length - 1][1];
    const bottomY = quads[quads.length - 1][2];
    const topY = quads[quads.length - 1][3];
    quads.push([endX, endX, bottomY, topY]);
  }
  return quads;
};

const groupCaretsByRow = (caretQuads: CaretQuadArray, indexBothSides: boolean) => {
  const caretsByRow = new Map<number, CaretPositionalData[]>();
  if (caretQuads.length === 0) return caretsByRow;
  caretQuads.forEach((caretQuad, index) => {
    const rowY = caretQuad[2];
    let rowCarets = caretsByRow.get(rowY);
    if (!rowCarets) {
      rowCarets = [];
      caretsByRow.set(rowY, rowCarets);
    }
    rowCarets.push({ position: new Vector2(caretQuad[0], rowY), index });
    // used for textPerRow for proper textLines extraction
    if (indexBothSides) {
      rowCarets.push({ position: new Vector2(caretQuad[1], rowY), index: index + 1 });
    }
  });
  return caretsByRow;
};

const getIndexesPerRow = (caretsByRow: Map<number, CaretPositionalData[]>) => {
  const indexesPerRow: Array<number[]> = [];
  if (caretsByRow === undefined) return indexesPerRow;
  caretsByRow.forEach((row: CaretPositionalData[]) => {
    const newRow: number[] = [];
    indexesPerRow.push(newRow);
    row.forEach((caret: CaretPositionalData) => {
      newRow.push(caret.index);
    });
  });
  return indexesPerRow;
};

const getTextPerRow = (caretQuads: CaretQuadArray, text: string) => {
  const caretsByRow = groupCaretsByRow(caretQuads, true);
  const indexesPerRow = getIndexesPerRow(caretsByRow);
  const lines: string[] = [];
  indexesPerRow.forEach((value: number[]) => {
    const newLine = text
      .substring(value[0], value[value.length - 1])
      .replace('\n', '')
      .trimEnd();
    lines.push(newLine);
  });

  return lines;
};

export const TEXT_SIZE_EPSILON = 0.01;

const getVisibleLinesCount = (
  config: TextAreaObjectConfig,
  measureMesh: Text,
  textPerRow: string[],
) => {
  const wrappingBox = new PlanogramBox().fromCenterAndSize(config.position, {
    x: config.scale[0],
    y: config.scale[1],
  });
  const availableHeight = wrappingBox.size(new Vector2()).y + TEXT_SIZE_EPSILON;
  const allLinesCount = textPerRow.length;

  let sceneFontSize: number;
  let sceneLineHeight: number;

  if (measureMesh.fontSize !== undefined) {
    sceneFontSize = Number(measureMesh.fontSize);
    const lineHeightRatio =
      measureMesh.lineHeight !== undefined
        ? Number(measureMesh.lineHeight)
        : config.lineHeight / config.fontSize;
    sceneLineHeight = sceneFontSize * lineHeightRatio;
  } else {
    sceneLineHeight = PLANOGRAM_CONFIG.fontUnitSize * config.lineHeight;
    sceneFontSize = PLANOGRAM_CONFIG.fontUnitSize * config.fontSize;
  }

  let visibleLinesCount = Math.floor(availableHeight / sceneLineHeight);
  visibleLinesCount = Math.min(visibleLinesCount, allLinesCount);

  if (
    config.verticalAlignment === TextVerticalAlignment.MIDDLE &&
    allLinesCount % 2 !== visibleLinesCount % 2 &&
    visibleLinesCount > 0
  ) {
    visibleLinesCount--;
  }

  return Math.max(0, visibleLinesCount);
};

export const getVisibleText = (
  measureMesh: Text,
  config: TextAreaObjectConfig,
): [text: string, overflowing: boolean] | undefined => {
  if (measureMesh?.textRenderInfo?.caretPositions === undefined) {
    return undefined;
  }

  const caretQuads = toCaretQuads(measureMesh.textRenderInfo.caretPositions);
  const textPerRow = getTextPerRow(caretQuads, config.text);
  if (config.autoFit) return [textPerRow.join('\n'), false];
  const visibleLinesCount = getVisibleLinesCount(config, measureMesh, textPerRow);

  const overflowing = visibleLinesCount < textPerRow.length;

  switch (config.verticalAlignment) {
    case TextVerticalAlignment.TOP:
      textPerRow.splice(visibleLinesCount);
      break;
    case TextVerticalAlignment.BOTTOM:
      textPerRow.splice(0, textPerRow.length - visibleLinesCount);
      break;
    case TextVerticalAlignment.MIDDLE: {
      const linesToCut = Math.ceil((textPerRow.length - visibleLinesCount) / 2);
      textPerRow.splice(0, linesToCut);
      textPerRow.splice(visibleLinesCount, linesToCut);
      break;
    }
    default:
  }

  return [textPerRow.join('\n'), overflowing];
};

export type TextSelectionRange = {
  startIndex: number;
  endIndex: number;
};

const isWhiteSpace = (char: string | undefined) =>
  char !== undefined && char !== '' && /\s/.test(char);

export enum Boundary {
  Start,
  End,
}

export const findWordBoundary = (text: string, index: number, boundary: Boundary) => {
  if (boundary === Boundary.Start) {
    let i = index;
    if (isWhiteSpace(text[i])) while (i > 0 && isWhiteSpace(text[i - 1])) i--;
    while (i > 0 && isWhiteSpace(text[i - 1])) i--;
    while (i > 0 && !isWhiteSpace(text[i - 1])) i--;
    return i;
  }
  let i = index;
  while (i < text.length && isWhiteSpace(text[i])) i++;
  while (i < text.length && !isWhiteSpace(text[i])) i++;
  return i;
};

const getTextRenderInfoAsync = (params: TypesetParams): Promise<TextRenderInfo> =>
  new Promise(resolve => getTextRenderInfo(params, resolve));

const MAX_ITERATIONS = 50;

export async function findMaxFontSizeAsync(
  mesh: Text,
  params: {
    containerSize: Vector2;
    maxFontSize: number;
    maxRows?: number;
    precision?: number; // percentage of maxFontSize
    overrides?: TypesetParams;
  },
): Promise<number> {
  if (mesh.textRenderInfo === null) return mesh.fontSize;
  const { containerSize, maxFontSize, maxRows, precision = 0.005, overrides = {} } = params;

  let lo = 0;
  let hi = maxFontSize;
  let iterations = 0;

  const fits = (info: TextRenderInfo): boolean => {
    const bounds = info.blockBounds;
    const width = bounds[2] - bounds[0];
    const height = bounds[3] - bounds[1];

    const sizeFits = width <= containerSize.x && height <= containerSize.y;
    if (!sizeFits) return false;

    if (maxRows !== undefined) {
      const caretQuads = toCaretQuads(info.caretPositions);
      const rows = groupCaretsByRow(caretQuads, false).size;
      if (rows > maxRows) return false;
    }
    return true;
  };

  while ((hi - lo) / hi > precision && iterations < MAX_ITERATIONS) {
    iterations++;
    const mid = (lo + hi) / 2;

    const renderinfo = await getTextRenderInfoAsync({
      ...mesh.textRenderInfo.parameters,
      font: mesh.font,
      ...overrides,
      fontSize: mid,
      maxWidth: overrides.maxWidth ?? containerSize.x,
    });

    fits(renderinfo) ? (lo = mid) : (hi = mid);
  }
  return lo;
}

export function textMeshRenderSize(mesh: Text): Vector2 {
  const result = new Vector2();
  if (mesh.textRenderInfo === null) return result;
  // TODO: this returns zero for non-empty texts with sufficiently large negative letter spacing
  const bounds = mesh.textRenderInfo.blockBounds;
  result.set(bounds[2] - bounds[0], bounds[3] - bounds[1]);
  return result;
}
